import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';

import { TOUR_SHOWING_STATUS_FAILED, TOUR_SHOWING_STATUS_COMPLETED } from 'common/tourConstants';
import {
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_LOCKBOX_TYPE_LOCKBOX,
  LISTING_LOCKBOX_TYPE_COMBO_LOCKBOX,
  LISTING_LOCKBOX_TYPE_BOTH,
  LISTING_PHOTO_DIMENSION_THUMBNAIL,
  LISTING_DATA_SOURCE_SUNROOM,
} from 'common/listingConstants';

import { getListingManagedPhotoUrls, getListingUrlPath } from 'common/listingHelpers';
import { getTourShowingLabel } from 'common/tourHelpers';
import getTourListingsGoogleMapsDirectionsUrl from 'common/util/getTourListingsGoogleMapsDirectionsUrl';
import { isUserAgentIphoneApp } from 'common/util/isUserAgentMobileApp';

import SrLink from 'client/components/SrLink/SrLink';
import SrExternalLink from 'client/components/SrExternalLink';

class SrAgentToursRowListing extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    showing: PropTypes.object.isRequired,
    tourId: PropTypes.number.isRequired,
    listingQuestions: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    userAgent: PropTypes.object.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
  };

  _onClickQuestionLink = (evt, listingUrl) => {
    evt.stopPropagation();
    evt.preventDefault();

    const { history } = this.props;
    history.push(`${listingUrl}?scrollTo=tourQuestions`);
  };

  render() {
    const {
      listing,
      showing,
      tourId,
      listingQuestions,
      userAgent,
      listingPhotoResolution,
    } = this.props;

    const listingQuestionsLength = listingQuestions.filter((question) => question.length > 0)
      .length;

    const listingUrl = getListingUrlPath(listing, { agentTourId: tourId });
    const managedPhotoUrls = getListingManagedPhotoUrls({
      listing: listing,
      dimension: LISTING_PHOTO_DIMENSION_THUMBNAIL,
      resolution: listingPhotoResolution,
    });

    const isSupraLockbox =
      (listing.lockboxType === LISTING_LOCKBOX_TYPE_LOCKBOX ||
        listing.lockboxType === LISTING_LOCKBOX_TYPE_BOTH ||
        listing.lockboxType === null) &&
      !listing.lockboxCode;

    let rightDom = null;

    // If showing marked as Not Available
    if (showing.note) {
      rightDom = null;
    }
    // If showing was a success or a fail
    else if (
      showing.status !== TOUR_SHOWING_STATUS_COMPLETED &&
      showing.status !== TOUR_SHOWING_STATUS_FAILED
    ) {
      const googleMapsDirectionsUrl = getTourListingsGoogleMapsDirectionsUrl({
        listings: [listing],
        showings: [showing],
        isIphoneApp: isUserAgentIphoneApp(userAgent),
      });

      rightDom = (
        <div>
          <SrExternalLink className="button grey directions" link={googleMapsDirectionsUrl}>
            get directions
          </SrExternalLink>
          {showing.wantsToApply && <span className="wantsToApplyLabel">Wants to Apply!</span>}
        </div>
      );
    } else {
      rightDom = (
        <div>
          <div
            className="questionsLink"
            onClick={(evt) => this._onClickQuestionLink(evt, listingUrl)}
          >
            {listingQuestionsLength === 0
              ? 'add question'
              : `${listingQuestionsLength} ${pluralize('question', listingQuestionsLength)}`}
          </div>
          {showing.wantsToApply && <span className="wantsToApplyLabel">Wants to Apply!</span>}
        </div>
      );
    }

    return (
      <div
        className={classnames('showingContainer clearfix', {
          notAvailable: showing.note,
          showingDone:
            showing.status === TOUR_SHOWING_STATUS_COMPLETED ||
            showing.status === TOUR_SHOWING_STATUS_FAILED,
          wantsToApply: showing.wantsToApply,
        })}
        key={listing.id}
      >
        <div className="description">
          <SrLink to={listingUrl} className="tourRowListingLink">
            <span
              className="thumbnail"
              style={{ backgroundImage: `url("${managedPhotoUrls[0]}")` }}
            >
              {showing.status === TOUR_SHOWING_STATUS_FAILED && !showing.note && (
                <div className="overlay failed" />
              )}
              {showing.status === TOUR_SHOWING_STATUS_COMPLETED && (
                <div className="overlay success" />
              )}
            </span>
            {listing.dataSource === LISTING_DATA_SOURCE_SUNROOM && <div className="sunroomIcon" />}
            <div className="descriptionContainer">
              <span className="address">
                {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                  ? listing.apartmentName
                  : `${listing.addressStreetDisplay} ${listing.addressStreetName}`}
              </span>
              {!showing.note && (
                <div>
                  {((listing.occupantType === 'Vacant' &&
                    !showing.isOccupantConfirmed &&
                    !showing.isAgentConfirmed) ||
                    !!listing.overrides.occupantType) && (
                    <div className="showingDetail vacant">Vacant</div>
                  )}
                  {!listing.overrides.occupantType && showing.isOccupantConfirmed !== null && (
                    <div className="showingDetail occupied">
                      {showing.isOccupantConfirmed ? 'Tenant Confirmed' : 'Tenant Pending'}
                    </div>
                  )}
                  {!listing.overrides.occupantType && showing.isAgentConfirmed !== null && (
                    <div className="showingDetail occupied">
                      {showing.isAgentConfirmed ? 'Agent Confirmed' : 'Agent Pending'}
                    </div>
                  )}
                  <div
                    className={classnames('showingDetail', {
                      supra: isSupraLockbox,
                      key:
                        listing.lockboxType === LISTING_LOCKBOX_TYPE_COMBO_LOCKBOX ||
                        listing.lockboxCode,
                    })}
                  >
                    {isSupraLockbox && <span className="text">Supra Lockbox</span>}
                    {listing.lockboxCode && (
                      <span className="text">Combo {listing.lockboxCode}</span>
                    )}
                  </div>
                </div>
              )}
              {showing.note && (
                <div className="semi-bold">
                  {getTourShowingLabel({ tourShowingStatus: showing.status, hasNote: true })}:{' '}
                  {showing.note}
                </div>
              )}
            </div>
            <div className="rightAction">{rightDom}</div>
          </SrLink>
        </div>
      </div>
    );
  }
}

SrAgentToursRowListing.displayName = 'SrAgentToursRowListing';

export default SrAgentToursRowListing;
